import React from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import type { IconType } from 'react-icons';
import InlineLoader from '../common/InlineLoader';
import { DashboardCampaignCard } from './DashboardCampaignCard';

interface CampaignScrollSectionProps {
  icon: IconType;
  title: string;
  campaigns: any[];
  isLoading: boolean;
  cardType?: 'scheduled' | 'unscheduled' | 'fourBigIdeas' | 'default';
  containerId: string;
  onCardClick: (campaignId: string, isOngoing: boolean) => void;
  onDelete?: (id: string) => void;
  onShare?: (campaign: any) => void;
  onReschedule?: (id: string) => void;
}

export const CampaignScrollSection: React.FC<CampaignScrollSectionProps> = ({
  icon: Icon,
  title,
  campaigns,
  isLoading,
  cardType = 'default',
  containerId,
  onCardClick,
  onDelete,
  onShare,
  onReschedule,
}) => {
  const scrollContainer = (direction: 'left' | 'right') => {
    const container = document.getElementById(containerId);
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Don't render if no campaigns and not loading
  if (!isLoading && campaigns.length === 0) {
    return null;
  }

  return (
    <div className="mb-3">
      <div className="inline-flex items-center gap-3 py-3">
        <Icon className="text-xl text-brand-primary" />
        <h2 className="text-xl font-normal text-white">
          {title} <span className="text-sm">({campaigns.length})</span>
        </h2>
      </div>
      
      <div className="relative mt-2">
        {!isLoading && campaigns.length > 4 && (
          <button
            onClick={() => scrollContainer('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 p-2 cursor-pointer rounded-full hover:bg-gray-700 transition-colors"
          >
            <MdChevronLeft size={24} className="text-white" />
          </button>
        )}
        
        {isLoading ? (
          <InlineLoader variant="campaign-cards" count={4} />
        ) : (
          <div
            id={containerId}
            className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="flex-shrink-0 w-[280px]">
                <DashboardCampaignCard
                  campaign={campaign}
                  cardType={cardType}
                  onCardClick={onCardClick}
                  onDelete={onDelete}
                  onShare={onShare ? () => onShare(campaign) : undefined}
                  onReschedule={onReschedule}
                />
              </div>
            ))}
          </div>
        )}
        
        {!isLoading && campaigns.length > 4 && (
          <button
            onClick={() => scrollContainer('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 p-2 cursor-pointer rounded-full hover:bg-gray-700 transition-colors"
          >
            <MdChevronRight size={24} className="text-white" />
          </button>
        )}
      </div>
    </div>
  );
};
